import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react'

type DiagnosticSessionSummary = {
  id: string
  userId: string
  clientActivityId: string | null
  appInstallId: string
  appSessionId: string | null
  trackingState: string
  platform: string
  appVersion: string
  buildNumber: string | null
  deviceModel: string | null
  osVersion: string | null
  locale: string | null
  locationIncluded: boolean
  startedAt: number | null
  submittedAt: number
  eventCount: number
  archivedAt: number | null
  archiveReason: string | null
}

type DiagnosticEvent = {
  seq: number
  type: string
  at: number
  trackingState: string | null
  message: string
  detail: string | null
  lat: number | null
  lng: number | null
  accuracyM: number | null
  speedMps: number | null
  recordedAt: number | null
  data: Record<string, unknown>
}

type DiagnosticSessionDetail = DiagnosticSessionSummary & {
  debugLogText: string
  events: DiagnosticEvent[]
}

type ApiEnvelope<T> = { data: T }

class ApiRequestError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

export default function App() {
  const [sessions, setSessions] = useState<DiagnosticSessionSummary[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<DiagnosticSessionDetail | null>(null)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [authRequired, setAuthRequired] = useState(false)
  const [loginUsername, setLoginUsername] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [loadingList, setLoadingList] = useState(true)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const selectedIdRef = useRef<string | null>(null)

  useEffect(() => {
    selectedIdRef.current = selectedId
  }, [selectedId])

  useEffect(() => {
    let cancelled = false
    fetchJson<DiagnosticSessionSummary[]>(
      `/api/admin/diagnostic-sessions?limit=80${includeArchived ? '&includeArchived=1' : ''}`,
    )
      .then((items) => {
        if (cancelled) return
        setSessions(items)
        const selectedStillVisible =
          selectedIdRef.current != null && items.some((item) => item.id === selectedIdRef.current)
        if (!selectedStillVisible) {
          const nextSelectedId = items[0]?.id ?? null
          setDetail(null)
          setLoadingDetail(nextSelectedId != null)
          selectedIdRef.current = nextSelectedId
          setSelectedId(nextSelectedId)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        if (err instanceof ApiRequestError && (err.status === 401 || err.status === 403)) {
          setAuthRequired(true)
          setSessions([])
          setSelectedId(null)
          setDetail(null)
        }
        setError(errorMessage(err))
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false)
      })
    return () => {
      cancelled = true
    }
  }, [includeArchived, reloadKey])

  useEffect(() => {
    if (!selectedId) return
    let cancelled = false
    fetchJson<DiagnosticSessionDetail>(
      `/api/admin/diagnostic-sessions/${encodeURIComponent(selectedId)}`,
    )
      .then((item) => {
        if (!cancelled) setDetail(item)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err))
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId])

  function selectSession(id: string) {
    setDetail(null)
    setLoadingDetail(true)
    selectedIdRef.current = id
    setSelectedId(id)
  }

  const stats = useMemo(() => {
    const active = sessions.filter((session) => !session.archivedAt).length
    const archived = sessions.length - active
    const eventCount = sessions.reduce((sum, session) => sum + session.eventCount, 0)
    return { active, archived, eventCount }
  }, [sessions])

  async function archiveSelected() {
    if (!detail || detail.archivedAt) return
    setError(null)
    try {
      await fetchJson(`/api/admin/diagnostic-sessions/${encodeURIComponent(detail.id)}/archive`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason: 'dashboard archive' }),
      })
      setIncludeArchived(true)
      setDetail({ ...detail, archivedAt: Date.now(), archiveReason: 'dashboard archive' })
      setSessions((current) =>
        current.map((session) =>
          session.id === detail.id
            ? { ...session, archivedAt: Date.now(), archiveReason: 'dashboard archive' }
            : session,
        ),
      )
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function submitAdminLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoginLoading(true)
    setError(null)
    try {
      await fetchJson('/api/admin/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ username: loginUsername, password: loginPassword }),
      })
      setLoginPassword('')
      setAuthRequired(false)
      setLoadingList(true)
      setReloadKey((value) => value + 1)
    } catch (err) {
      setLoginPassword('')
      setError(errorMessage(err))
    } finally {
      setLoginLoading(false)
    }
  }

  return (
    <main className="appShell">
      <header className="topBar">
        <div>
          <p className="eyebrow">Runner Activity Data</p>
          <h1>Diagnostic Sessions</h1>
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => {
              setLoadingList(true)
              setError(null)
              setIncludeArchived(event.target.checked)
            }}
          />
          보관 포함
        </label>
      </header>

      {error && (
        <div className="errorBanner" role="alert">
          {error}
        </div>
      )}

      {authRequired ? (
        <AdminLoginPanel
          username={loginUsername}
          password={loginPassword}
          loading={loginLoading}
          onUsernameChange={setLoginUsername}
          onPasswordChange={setLoginPassword}
          onSubmit={submitAdminLogin}
        />
      ) : (
        <>
      <section className="summaryBand" aria-label="요약">
        <Metric label="활성" value={stats.active} />
        <Metric label="보관" value={stats.archived} />
        <Metric label="이벤트" value={stats.eventCount} />
      </section>

      <section className="workspace">
        <div className="listPane">
          <div className="paneHeader">
            <h2>수집 목록</h2>
            <span>{loadingList ? '불러오는 중' : `${sessions.length}건`}</span>
          </div>
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>제출</th>
                  <th>상태</th>
                  <th>플랫폼</th>
                  <th>사용자</th>
                  <th>이벤트</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr
                    key={session.id}
                    className={session.id === selectedId ? 'selected' : ''}
                    onClick={() => selectSession(session.id)}
                  >
                    <td>
                      <button type="button" className="rowButton">
                        {dateTime(session.submittedAt)}
                      </button>
                      {session.archivedAt && <span className="badge muted">보관</span>}
                    </td>
                    <td>{session.trackingState}</td>
                    <td>
                      {session.platform}
                      <span className="subtle"> {session.appVersion}</span>
                    </td>
                    <td className="mono">{session.userId}</td>
                    <td>{session.eventCount}</td>
                  </tr>
                ))}
                {!loadingList && sessions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="emptyCell">
                      표시할 진단 세션이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="detailPane">
          <div className="paneHeader">
            <h2>상세</h2>
            <button
              type="button"
              className="archiveButton"
              onClick={archiveSelected}
              disabled={!detail || Boolean(detail.archivedAt)}
            >
              보관
            </button>
          </div>
          {loadingDetail && <p className="loading">상세를 불러오는 중입니다.</p>}
          {!loadingDetail && detail && <SessionDetail session={detail} />}
          {!loadingDetail && !detail && <p className="loading">세션을 선택하세요.</p>}
        </div>
      </section>
        </>
      )}
    </main>
  )
}

function AdminLoginPanel({
  username,
  password,
  loading,
  onUsernameChange,
  onPasswordChange,
  onSubmit,
}: {
  username: string
  password: string
  loading: boolean
  onUsernameChange: (value: string) => void
  onPasswordChange: (value: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}) {
  return (
    <section className="loginPanel" aria-label="관리자 로그인">
      <form onSubmit={onSubmit}>
        <label htmlFor="adminUsername">관리자 ID</label>
        <input
          id="adminUsername"
          type="text"
          value={username}
          autoComplete="username"
          onChange={(event) => onUsernameChange(event.target.value)}
        />
        <label htmlFor="adminPassword">비밀번호</label>
        <div className="loginActions">
          <input
            id="adminPassword"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => onPasswordChange(event.target.value)}
          />
          <button
            type="submit"
            disabled={loading || username.trim().length === 0 || password.length === 0}
          >
            {loading ? '확인 중' : '접속'}
          </button>
        </div>
      </form>
    </section>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value.toLocaleString('ko-KR')}</strong>
    </div>
  )
}

function SessionDetail({ session }: { session: DiagnosticSessionDetail }) {
  return (
    <div className="detailContent">
      <dl className="metaGrid">
        <Meta label="세션" value={session.id} mono />
        <Meta label="사용자" value={session.userId} mono />
        <Meta label="활동" value={session.clientActivityId ?? '없음'} mono />
        <Meta label="설치" value={session.appInstallId} mono />
        <Meta label="상태" value={session.trackingState} />
        <Meta label="제출" value={dateTime(session.submittedAt)} />
        <Meta label="위치" value={session.locationIncluded ? '포함' : '미포함'} />
        <Meta label="기기" value={[session.deviceModel, session.osVersion].filter(Boolean).join(' · ')} />
      </dl>

      <section className="detailSection">
        <h3>구조화 이벤트</h3>
        <div className="eventTableWrap">
          <table className="eventTable">
            <thead>
              <tr>
                <th>#</th>
                <th>시각</th>
                <th>타입</th>
                <th>메시지</th>
                <th>좌표</th>
              </tr>
            </thead>
            <tbody>
              {session.events.map((event) => (
                <tr key={`${session.id}-${event.seq}`}>
                  <td>{event.seq}</td>
                  <td>{dateTime(event.at)}</td>
                  <td className="mono">{event.type}</td>
                  <td>
                    {event.message}
                    {event.detail && <span className="subtle"> · {event.detail}</span>}
                  </td>
                  <td className="mono">
                    {event.lat != null && event.lng != null
                      ? `${event.lat.toFixed(6)}, ${event.lng.toFixed(6)}`
                      : 'n/a'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="detailSection">
        <h3>원문 로그</h3>
        <pre>{session.debugLogText || '원문 로그가 없습니다.'}</pre>
      </section>
    </div>
  )
}

function Meta({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? 'mono' : undefined}>{value || 'n/a'}</dd>
    </div>
  )
}

async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  for (const [key, value] of Object.entries(csrfHeaders(init?.method ?? 'GET'))) {
    headers.set(key, value)
  }
  const res = await fetch(input, {
    credentials: 'include',
    ...init,
    headers,
  })
  const body = (await res.json().catch(() => null)) as ApiEnvelope<T> | { error?: { message?: string } } | null
  if (!res.ok) {
    throw new ApiRequestError(
      (body && 'error' in body ? body.error?.message : null) ?? `요청 실패 (${res.status})`,
      res.status,
    )
  }
  return (body as ApiEnvelope<T>).data
}

function csrfHeaders(method: string): Record<string, string> {
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) return {}
  const token = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith('activity_admin_csrf='))
    ?.split('=')[1]
  return token ? { 'x-csrf-token': decodeURIComponent(token) } : {}
}

function dateTime(value: number | null): string {
  if (!value) return 'n/a'
  return new Intl.DateTimeFormat('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'
}
